// Plan year date helpers — date-only ISO strings, TZ-safe rendering.
//
// The BE serialises plan-year dates as ``YYYY-MM-DD`` strings.
// ``new Date("2026-12-31")`` parses those as UTC midnight, so
// ``toLocaleDateString`` in any timezone west of UTC bumps the
// rendered day back by one ("Dec 30, 2026" instead of "Dec 31").
// Always parse YYYY-MM-DD as local-midnight so what we display
// matches the value the user typed in the form.

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;


/** Parse a YYYY-MM-DD ISO date as local-midnight. Returns ``null``
 * for missing or unparseable input so callers can render a dash. */
export function parsePlanYearDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = DATE_ONLY_RE.exec(iso);
  if (!m) {
    const fallback = new Date(iso);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  const [, year, month, day] = m;
  return new Date(Number(year), Number(month) - 1, Number(day));
}


/** Render a YYYY-MM-DD ISO date as "Jan 1, 2026" in the user's
 * locale. Returns "—" for missing input. */
export function formatPlanYearDate(iso: string | null | undefined): string {
  const d = parsePlanYearDate(iso);
  if (d === null) return iso || "—";
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}


/** Render an inclusive coverage window as "Jan 1, 2026 – Dec 31, 2026". */
export function formatPlanYearRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  return `${formatPlanYearDate(start)} – ${formatPlanYearDate(end)}`;
}
